<template>
  <div class="coloring-game">
    <div v-if="isDrawingComplete">
      <!-- Removed LevelComplete component -->
      <div class="level-complete-message">
        <h2>Drawing Complete!</h2>
        <p>Great job on completing your drawing!</p>
      </div>
    </div>
    <div v-else>
      <div class="palette">
        <div
          v-for="(color, index) in colors"
          :key="index"
          :style="{ backgroundColor: color }"
          class="color-swatch"
          @click="selectColor(color)"
        />
      </div>

      <div class="brush-size-control">
        <label for="brushSize">Brush Size:</label>
        <div class="brush-size-display">
          <input
            id="brushSize"
            v-model="brushSize"
            type="range"
            min="1"
            max="50"
          >
          <div
            class="brush-size-ball"
            :style="{
              width: brushSize + 'px',
              height: brushSize + 'px',
              backgroundColor: selectedColor,
            }"
          />
        </div>
      </div>

      <div class="canvas-container">
        <canvas
          ref="drawingCanvas"
          class="canvas"
          @mousedown="startDrawing"
          @mousemove="onMouseMove"
          @mouseup="stopDrawing"
          @mouseleave="handleMouseLeave"
          @mouseenter="handleMouseEnter"
          @touchstart.prevent="startDrawing"
          @touchmove.prevent="onTouchMove"
          @touchend.prevent="stopDrawing"
          @touchcancel.prevent="stopDrawing"
        />
        <canvas
          ref="imageCanvas"
          class="canvas image-canvas"
        />
        <canvas
          ref="cursorCanvas"
          class="canvas cursor-canvas"
        />
      </div>
      <div class="button-group">
        <button
          class="undo-button"
          :disabled="!canUndo"
          @click="undoDrawing"
        >
          Undo ↩️
        </button>
        <button
          class="finish-button"
          @click="completeDrawing"
        >
          Finish Drawing
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin";
export default {
  mixins: [speechMixin],
  data() {
    return {
      colors: [
        "red",
        "blue",
        "green",
        "yellow",
        "purple",
        "orange",
        "pink",
        "brown",
        "black",
        "white", // Added white color
      ],
      selectedColor: "#000000",
      isDrawing: false,
      isDrawingComplete: false,
      brushSize: 15,
      cursorX: 0,
      cursorY: 0,
      mouseInCanvas: false,
      undoStack: [],
      maxUndoStackSize: 20,
    };
  },
  computed: {
    canUndo() {
      return this.undoStack.length > 0;
    },
  },
  watch: {
    brushSize() {
      if (this.mouseInCanvas) {
        this.drawCursorIndicator();
      }
    },
    selectedColor() {
      if (this.mouseInCanvas) {
        this.drawCursorIndicator();
      }
    },
  },
  mounted() {
    // Set canvas dimensions (removed from reactive data)
    this.canvasWidth = 500;
    this.canvasHeight = 500;
    this.initializeCanvases();
    this.setupVoices();
  },
  methods: {
    initializeCanvases() {
      const drawingCanvas = this.$refs.drawingCanvas;
      const imageCanvas = this.$refs.imageCanvas;
      const cursorCanvas = this.$refs.cursorCanvas;

      drawingCanvas.width = this.canvasWidth;
      drawingCanvas.height = this.canvasHeight;
      imageCanvas.width = this.canvasWidth;
      imageCanvas.height = this.canvasHeight;
      cursorCanvas.width = this.canvasWidth;
      cursorCanvas.height = this.canvasHeight;

      this.drawingCtx = drawingCanvas.getContext("2d");
      this.imageCtx = imageCanvas.getContext("2d");
      this.cursorCtx = cursorCanvas.getContext("2d");

      // For smoother lines
      this.drawingCtx.lineCap = "round";
      this.drawingCtx.lineJoin = "round";
      // Set paint opacity to 70%
      this.drawingCtx.globalAlpha = 0.7;
      this.cursorCtx.globalAlpha = 0.7;

      // Load the image onto the imageCanvas
      this.loadImage();
    },
    loadImage() {
      const image = new Image();
      image.crossOrigin = "Anonymous"; // Avoid CORS issues
      image.src =
        "https://static.vecteezy.com/system/resources/previews/034/556/153/non_2x/coloring-pages-for-kids-ai-generative-png.png"; // Replace with your PNG image URL
      image.onload = () => {
        this.imageCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.imageCtx.drawImage(
          image,
          0,
          0,
          this.canvasWidth,
          this.canvasHeight
        );
      };
    },
    selectColor(color) {
      this.speakOutLoud(color);
      this.selectedColor = color;
    },
    startDrawing(event) {
      this.isDrawing = true;
      const { x, y } = this.getCanvasCoords(event);
      this.drawingCtx.beginPath();
      this.drawingCtx.moveTo(x, y);
    },
    draw(event) {
      if (!this.isDrawing) return;
      const { x, y } = this.getCanvasCoords(event);
      this.drawingCtx.lineTo(x, y);
      this.drawingCtx.strokeStyle = this.selectedColor;
      this.drawingCtx.lineWidth = this.brushSize;
      // Ensure paint opacity is 70%
      this.drawingCtx.globalAlpha = 0.7;
      this.drawingCtx.stroke();
      this.drawingCtx.beginPath();
      this.drawingCtx.moveTo(x, y);
    },
    stopDrawing() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      this.drawingCtx.closePath();

      // Save current state to undo stack
      this.saveState();
    },
    saveState() {
      const imageData = this.drawingCtx.getImageData(
        0,
        0,
        this.canvasWidth,
        this.canvasHeight
      );
      this.undoStack.push(imageData);

      // Limit undo stack size
      if (this.undoStack.length > this.maxUndoStackSize) {
        this.undoStack.shift();
      }
    },
    undoDrawing() {
      if (!this.canUndo) return;
      this.undoStack.pop(); // Remove current state

      const previousState = this.undoStack[this.undoStack.length - 1];

      // Clear the canvas
      this.drawingCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      if (previousState) {
        // Restore previous state
        this.drawingCtx.putImageData(previousState, 0, 0);
      }
    },
    getCanvasCoords(event) {
      const rect = this.$refs.drawingCanvas.getBoundingClientRect();
      let clientX, clientY;
      if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      return {
        x: ((clientX - rect.left) / rect.width) * this.canvasWidth,
        y: ((clientY - rect.top) / rect.height) * this.canvasHeight,
      };
    },
    onMouseMove(event) {
      this.draw(event);
      this.updateCursor(event);
    },
    onTouchMove(event) {
      this.draw(event);
      this.updateCursor(event);
    },
    handleMouseEnter(event) {
      this.mouseInCanvas = true;
      this.updateCursor(event);
    },
    handleMouseLeave() {
      this.mouseInCanvas = false;
      this.clearCursorCanvas();
    },
    updateCursor(event) {
      const { x, y } = this.getCanvasCoords(event);
      this.cursorX = x;
      this.cursorY = y;
      this.drawCursorIndicator();
    },
    drawCursorIndicator() {
      const ctx = this.cursorCtx;
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      if (this.mouseInCanvas) {
        ctx.beginPath();
        ctx.arc(this.cursorX, this.cursorY, this.brushSize / 2, 0, Math.PI * 2);
        ctx.strokeStyle = "black";
        ctx.fillStyle = this.selectedColor;
        ctx.lineWidth = 1;
        // Ensure cursor opacity is 70%
        ctx.globalAlpha = 0.7;
        ctx.stroke();
        ctx.fill();
      }
    },
    clearCursorCanvas() {
      this.cursorCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    },
    completeDrawing() {
      this.isDrawingComplete = true;
      setTimeout(() => {
        this.$router.push("/colors");
      }, 4000);
    },
  },
};
</script>

<style scoped>
.coloring-game {
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
}

.palette {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.color-swatch {
  width: 60px;
  height: 60px;
  margin: 5px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid #ccc;
}
@media (max-width: 767px) {
  .color-swatch {
    width: 40px;
    height: 40px;
  }
}

.brush-size-control {
  margin-bottom: 20px;
}

.brush-size-control label {
  display: block;
  margin-bottom: 5px;
}

.brush-size-ball {
  border-radius: 50%;
  margin-left: 10px;
}

.brush-size-display {
  display: flex;
  align-items: center;
  justify-content: center;
}

.canvas-container {
  position: relative;
  max-width: 400px;
  margin: 0 auto 20px;
  border: 2px solid #ccc;
  background-color: white;
  height: 500px;
}
@media (max-width: 767px) {
  .canvas-container {
    max-width: 300px;
    height: 400px;
  }
}

.canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.image-canvas,
.cursor-canvas {
  pointer-events: none;
}

.button-group {
  display: flex;
  justify-content: center;
  margin: 10px;
}

.undo-button,
.finish-button {
  padding: 10px 20px;
  margin: 10px;
  font-size: 1rem;
  cursor: pointer;
}

.undo-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

h1 {
  font-size: 2rem;
  margin-bottom: 20px;
}

.level-complete-message {
  margin-top: 50px;
}

.level-complete-message h2 {
  font-size: 2rem;
  color: green;
}

.level-complete-message p {
  font-size: 1.2rem;
}
</style>
